import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  BookOpen,
  User,
  ArrowLeft,
  Play,
  Pause,
  CheckCircle,
  Users,
  AlertCircle,
  FileText,
  Download,
  Package,
  Edit3,
  Save,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CourseData } from "@packages/constant/CourseData";
import { TextLinear } from "@core/text";
import {
  LessonHookResponseDto,
  TedLessonPlannerResponseDto,
  TedLessonPlannerSteps,
  TedLessonPlannerUpdateRequestDto,
} from "@packages/api/madlenAPI.schemas";
import {
  getGetTedLessonPlannerQueryKey,
  updateTedLessonPlanner,
} from "@packages/api/madlenAPI";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import HookCardPDF from "@features/tools/components/hooks/HookCardPdf";
import MisconceptionCardPDF from "@features/tools/components/misconceptions/MisconceptionCardPdf";
import LoadingContainer from "@packages/components/Loading";
import CitationParser from "./CitationParser";
import CitationModal from "./CitationModal";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  fillTemplate,
  saveFile,
  transformTedPlanToTemplateData,
} from "./DocxGenerator";

// Extended TED plan type with additional fields for display
interface ExtendedTedPlan extends TedLessonPlannerResponseDto {
  createdAt?: string;
  updatedAt?: string;
  courseType?: string;
}

// Parser to handle actual API data structure and convert to expected format
interface ParsedMaterial {
  ek_id: number;
  tool_id?: number;
  icerik_json: {
    title: string;
    description?: string | null;
    hooks?: Array<{
      title: string;
      description: string;
      expected_outcome: string;
      materials_needed: string;
    }>;
    misconceptions?: Array<{
      misconception: string;
      clarification: string;
      why_misconception_occurs: string;
      fix_strategies: string;
      real_classroom_example: string;
    }>;
  };
  toolType: string;
}

// Function to parse the API response into a consistent format
const parseMaterialFromAPI = (material: any): ParsedMaterial | null => {
  if (!material || !material.icerik_json) return null;

  try {
    if (
      material.toolType === "LESSON_HOOK" &&
      Array.isArray(material.icerik_json)
    ) {
      const hooks = material.icerik_json[1] || [];
      return {
        ek_id: material.ek_id,
        tool_id: material.tool_id,
        icerik_json: {
          title: hooks.length > 0 ? hooks[0].title : "Lesson Hook",
          description: hooks.length > 0 ? hooks[0].description : null,
          hooks: hooks,
        },
        toolType: material.toolType,
      };
    } else if (
      material.toolType === "COMMON_MISCONCEPTION" &&
      !Array.isArray(material.icerik_json)
    ) {
      const misconceptions = material.icerik_json.misconceptions || [];
      return {
        ek_id: material.ek_id,
        tool_id: material.tool_id,
        icerik_json: {
          title:
            misconceptions.length > 0
              ? misconceptions[0].misconception
              : "Common Misconception",
          description:
            misconceptions.length > 0 ? misconceptions[0].clarification : null,
          misconceptions: misconceptions,
        },
        toolType: material.toolType,
      };
    }
  } catch (error) {
    console.error("Error parsing material:", error);
  }

  return null;
};

interface LessonGroup {
  lessonNumber: number;
  steps: TedLessonPlannerSteps[];
  totalDuration: number;
  stepsByTimePeriod: {
    ders_oncesi: TedLessonPlannerSteps[];
    ders_ici: TedLessonPlannerSteps[];
    ders_sonrasi: TedLessonPlannerSteps[];
  };
}

interface TedPlanDetailsProps {
  plan: ExtendedTedPlan;
}

interface EditingStep {
  id: number;
  yontem_teknik: string;
  icerik: string;
  sure_dakika: number;
}

const TedPlanDetails: React.FC<TedPlanDetailsProps> = ({ plan }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedLessons, setExpandedLessons] = useState<number[]>([]);
  const [selectedCitation, setSelectedCitation] = useState<{
    material: ParsedMaterial;
    description: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingStep, setEditingStep] = useState<EditingStep | null>(null);
  const editingRef = useRef<HTMLDivElement>(null);

  // Handler functions for edit operations
  const handleEditStep = (step: TedLessonPlannerSteps) => {
    setEditingStep({
      id: step.id!,
      yontem_teknik: step.yontem_teknik || "",
      icerik: step.icerik || "",
      sure_dakika: step.sure_dakika || 0,
    });
  };

  const handleSaveStep = () => {
    if (!editingStep || !plan.id) return;

    // Find the original step to update
    const updatedSteps =
      plan.steps?.map((step) => {
        if (step.id === editingStep.id) {
          return {
            ...step,
            yontem_teknik: editingStep.yontem_teknik,
            icerik: editingStep.icerik,
            sure_dakika: editingStep.sure_dakika,
          };
        }
        return step;
      }) || [];

    // Update the plan with the modified steps
    const updateData: TedLessonPlannerUpdateRequestDto = {
      name: plan.name,
      steps: updatedSteps,
    };

    updatePlanMutation.mutate({ id: plan.id, data: updateData });
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
  };

  const handleEditFieldChange = (
    field: keyof EditingStep,
    value: string | number,
  ) => {
    if (!editingStep) return;
    setEditingStep({ ...editingStep, [field]: value });
  };

  // Mutation for updating TED lesson planner
  const updatePlanMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: TedLessonPlannerUpdateRequestDto;
    }) => updateTedLessonPlanner(id, data),
    onSuccess: () => {
      // Invalidate and refetch the plan data
      queryClient.invalidateQueries({
        queryKey: getGetTedLessonPlannerQueryKey(plan.id!),
      });
      setEditingStep(null);
    },
    onError: (error) => {
      console.error("Error updating lesson plan:", error);
      // You might want to show a toast notification here
    },
  });

  // Handle click outside to save changes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editingStep &&
        editingRef.current &&
        !editingRef.current.contains(event.target as Node)
      ) {
        handleSaveStep();
      }
    };

    if (editingStep) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [editingStep, handleSaveStep]);

  // Parse materials from the actual API response
  const parsedMaterials = useMemo(() => {
    if (!plan.additionalMaterials) return [];
    return plan.additionalMaterials
      .map(parseMaterialFromAPI)
      .filter((material): material is ParsedMaterial => material !== null);
  }, [plan.additionalMaterials]);

  // Check if plan data is properly loaded
  if (!plan || !plan.steps || !Array.isArray(plan.steps)) {
    return (
      <LoadingContainer
        title={t("tedPlan.loading")}
        themeColor="blue"
        fullHeight={true}
      />
    );
  }

  // Group steps by lesson number
  const lessonGroups: LessonGroup[] = useMemo(() => {
    const groups: { [key: number]: TedLessonPlannerSteps[] } = {};

    // Additional safety check
    if (!plan.steps || !Array.isArray(plan.steps)) {
      return [];
    }

    plan.steps.forEach((step) => {
      const lessonNo = step.ders_no || 1; // Default to lesson 1 if undefined
      if (!groups[lessonNo]) {
        groups[lessonNo] = [];
      }
      groups[lessonNo].push(step);
    });

    return Object.entries(groups)
      .map(([lessonNumber, steps]) => {
        const totalDuration = steps.reduce(
          (sum, step) => sum + (step.sure_dakika || 0),
          0,
        );

        const stepsByTimePeriod = {
          ders_oncesi: steps.filter(
            (step) => step.zaman_dilimi === "ders_oncesi",
          ),
          ders_ici: steps.filter((step) => step.zaman_dilimi === "ders_ici"),
          ders_sonrasi: steps.filter(
            (step) => step.zaman_dilimi === "ders_sonrasi",
          ),
        };

        return {
          lessonNumber: parseInt(lessonNumber),
          steps,
          totalDuration,
          stepsByTimePeriod,
        };
      })
      .sort((a, b) => a.lessonNumber - b.lessonNumber);
  }, [plan.steps]);

  const toggleLessonExpansion = (lessonNumber: number) => {
    setExpandedLessons((prev) =>
      prev.includes(lessonNumber)
        ? prev.filter((num) => num !== lessonNumber)
        : [...prev, lessonNumber],
    );
  };

  const handleCitationClick = (
    material: ParsedMaterial,
    description: string,
  ) => {
    setSelectedCitation({ material, description });
  };

  const handleMaterialClick = (material: ParsedMaterial) => {
    setSelectedCitation({
      material,
      description: material.icerik_json.title,
    });
  };

  const handleDownloadMaterial = async (material: ParsedMaterial) => {
    try {
      // Default PDF customization settings
      const defaultCustomizationSettings: PdfCustomizationSettings = {
        showSchoolLogo: true,
        schoolLogoPosition: "topRight",
        madlenLogoPosition: "center",
        showDate: true,
        showTeacherName: true,
        showGeneratedImage: true,
        fontSize: "medium",
      };

      let pdfDoc;
      let filename;
      console.log(material.toolType === "LESSON_HOOK");

      if (material.toolType === "LESSON_HOOK") {
        // Convert to LessonHookResponseDto format
        const lessonHookData = {
          id: material.ek_id,
          title: "",
          description: material.icerik_json.description || "",
          courseName: plan.courseType,
          gradeLevel: plan.grade,
          hooks: material.icerik_json.hooks || [],
          createdAt: plan.createdAt,
          isPublic: false,
          authorName: "",
          approvalStatus: "APPROVED",
          authorId: 0,
          likeCount: 0,
        } as LessonHookResponseDto;

        pdfDoc = (
          <HookCardPDF
            lessonHook={lessonHookData}
            customizationSettings={defaultCustomizationSettings}
          />
        );
        filename = `${material.icerik_json.title.replace(/[^a-zA-Z0-9]/g, "_")}_lesson_hook.pdf`;
      } else if (material.toolType === "COMMON_MISCONCEPTION") {
        // Convert to Misconception format
        const misconceptionData = {
          courseName: plan.courseType,
          gradeLevel: plan.grade,
          title: "",
          misconceptions: material.icerik_json.misconceptions || [],
          imageUrl: undefined,
          authorName: undefined,
          createdAt: plan.createdAt,
        };

        pdfDoc = (
          <MisconceptionCardPDF
            misconception={misconceptionData}
            customizationSettings={defaultCustomizationSettings}
          />
        );
        filename = `${material.icerik_json.title.replace(/[^a-zA-Z0-9]/g, "_")}_misconceptions.pdf`;
      }

      if (pdfDoc && filename) {
        // Generate PDF blob
        const pdfBlob = await pdf(pdfDoc).toBlob();

        // Create download link
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Fallback to text download if PDF generation fails
      handleDownloadMaterialAsText(material);
    }
  };

  const handleDownloadMaterialAsText = (material: ParsedMaterial) => {
    // Fallback text download method for individual material
    let materialContent = `# ${material.icerik_json.title}\n\n`;

    if (material.icerik_json.description) {
      materialContent += `${material.icerik_json.description}\n\n`;
    }

    if (
      material.toolType === "LESSON_HOOK" &&
      Array.isArray(material.icerik_json)
    ) {
      materialContent += `## ${t("tedPlan.materialType.lessonHook")}\n\n`;
      (material.icerik_json.hooks || []).forEach((hook, index) => {
        materialContent += `### ${hook.title}\n\n`;
        materialContent += `**${t("tedPlan.modal.description")}:** ${hook.description}\n\n`;
        materialContent += `**${t("tedPlan.modal.expectedOutcome")}:** ${hook.expected_outcome}\n\n`;
        materialContent += `**${t("tedPlan.modal.materialsNeeded")}:** ${hook.materials_needed}\n\n`;
        if (
          Array.isArray(material.icerik_json) &&
          index < (material.icerik_json.hooks || []).length - 1
        ) {
          materialContent += `---\n\n`;
        }
      });
    }

    if (
      material.toolType === "COMMON_MISCONCEPTION" &&
      !Array.isArray(material.icerik_json)
    ) {
      materialContent += `## ${t("tedPlan.materialType.misconception")}\n\n`;
      (material.icerik_json.misconceptions || []).forEach(
        (misconception, index) => {
          materialContent += `**${t("tedPlan.modal.misconception")}:** ${misconception.misconception}\n\n`;
          materialContent += `**${t("tedPlan.modal.clarification")}:** ${misconception.clarification}\n\n`;
          materialContent += `**${t("tedPlan.modal.whyOccurs")}:** ${misconception.why_misconception_occurs}\n\n`;
          materialContent += `**${t("tedPlan.modal.fixStrategies")}:** ${misconception.fix_strategies}\n\n`;
          materialContent += `**${t("tedPlan.modal.realExample")}:** ${misconception.real_classroom_example}\n\n`;
          if (
            !Array.isArray(material.icerik_json) &&
            index < (material.icerik_json.misconceptions || []).length - 1
          ) {
            materialContent += `---\n\n`;
          }
        },
      );
    }

    const blob = new Blob([materialContent], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${material.icerik_json.title.replace(/[^a-zA-Z0-9]/g, "_")}_material.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadLessonPlan = async () => {
    setIsLoading(true);

    try {
      // Template dosyasını yükle
      const response = await fetch("/docx/ted-tymm.docx");
      if (!response.ok) {
        throw new Error(`Template dosyası yüklenemedi: ${response.statusText}`);
      }
      const templateArrayBuffer = await response.arrayBuffer();

      // TED plan verilerini template formatına dönüştür
      const templateData = transformTedPlanToTemplateData(t, plan);

      // Template'i doldur
      const filledBlob = fillTemplate(templateArrayBuffer, templateData);

      // Dosyayı indir
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `ted_plan_${plan.name || "ders_plani"}_${timestamp}.docx`;
      saveFile(filledBlob, filename);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimePeriodIcon = (timePeriod: string) => {
    switch (timePeriod) {
      case "ders_oncesi":
        return <Pause className="w-4 h-4" />;
      case "ders_ici":
        return <Play className="w-4 h-4" />;
      case "ders_sonrasi":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getTimePeriodLabel = (timePeriod: string) => {
    switch (timePeriod) {
      case "ders_oncesi":
        return t("tedPlan.timePeriod.beforeClass");
      case "ders_ici":
        return t("tedPlan.timePeriod.duringClass");
      case "ders_sonrasi":
        return t("tedPlan.timePeriod.afterClass");
      default:
        return timePeriod;
    }
  };

  const getTimePeriodColor = (timePeriod: string) => {
    switch (timePeriod) {
      case "ders_oncesi":
        return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700";
      case "ders_ici":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
      case "ders_sonrasi":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  const totalPlanDuration = (plan.steps || []).reduce(
    (sum, step) => sum + (step.sure_dakika || 0),
    0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
      <div className="container max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/lesson-planning-tools/ted-plan")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </button>

          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="space-y-2">
                <TextLinear
                  variant="heroTitle"
                  className="leading-tight text-2xl lg:text-3xl"
                  from="from-blue-600 dark:from-blue-400"
                  to="to-blue-800 dark:to-blue-300"
                >
                  {plan.name}
                </TextLinear>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>
                      {t("common.grade")} {plan.grade}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    <span>
                      {t(
                        `courseType.${CourseData[plan.courseType as keyof typeof CourseData]?.name(plan.grade || 1) || plan.courseType}`,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>
                      {totalPlanDuration} {t("common.time.minutes")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {lessonGroups.length} {t("tedPlan.lessons")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Lessons */}
        <div className="space-y-6">
          {lessonGroups.map((lesson) => {
            const isExpanded = expandedLessons.includes(lesson.lessonNumber);

            return (
              <motion.div
                key={lesson.lessonNumber}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                {/* Lesson Header */}
                <button
                  onClick={() => toggleLessonExpansion(lesson.lessonNumber)}
                  className="w-full p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t("tedPlan.lesson")} {lesson.lessonNumber}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>
                            {lesson.totalDuration} {t("common.time.minutes")}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>
                            {lesson.steps.length} {t("common.steps")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {isExpanded ? "" : t("common.expand")}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Lesson Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <div className="p-6 space-y-6">
                        {/* Time Periods */}
                        {Object.entries(lesson.stepsByTimePeriod).map(
                          ([timePeriod, steps]) => {
                            if (steps.length === 0) return null;

                            return (
                              <div key={timePeriod} className="space-y-4">
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`flex items-center gap-1 px-3 py-1 rounded-lg border text-sm font-medium ${getTimePeriodColor(timePeriod)}`}
                                  >
                                    {getTimePeriodIcon(timePeriod)}
                                    <span>
                                      {getTimePeriodLabel(timePeriod)}
                                    </span>
                                  </div>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {steps.reduce(
                                      (sum, step) =>
                                        sum + (step.sure_dakika || 0),
                                      0,
                                    )}{" "}
                                    {t("common.time.minutes")}
                                  </span>
                                </div>

                                <div className="space-y-3 pl-4">
                                  {steps.map((step) => {
                                    const isEditing =
                                      editingStep?.id === step.id;

                                    return (
                                      <div
                                        key={step.id}
                                        ref={isEditing ? editingRef : null}
                                        className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3"
                                      >
                                        {isEditing && editingStep ? (
                                          // Edit mode
                                          <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                              <input
                                                type="text"
                                                value={
                                                  editingStep.yontem_teknik
                                                }
                                                onChange={(e) =>
                                                  handleEditFieldChange(
                                                    "yontem_teknik",
                                                    e.target.value,
                                                  )
                                                }
                                                className="flex-1 px-3 py-2 text-sm font-medium bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
                                                placeholder={t(
                                                  "tedPlan.edit.methodTechnique",
                                                )}
                                              />
                                              <div className="flex items-center gap-2 ml-3">
                                                <input
                                                  type="number"
                                                  value={
                                                    editingStep.sure_dakika
                                                  }
                                                  onChange={(e) =>
                                                    handleEditFieldChange(
                                                      "sure_dakika",
                                                      parseInt(
                                                        e.target.value,
                                                      ) || 0,
                                                    )
                                                  }
                                                  className="w-16 px-2 py-1 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
                                                  min="0"
                                                />
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                  {t("common.time.minutes")}
                                                </span>
                                              </div>
                                            </div>

                                            <textarea
                                              value={editingStep.icerik}
                                              onChange={(e) =>
                                                handleEditFieldChange(
                                                  "icerik",
                                                  e.target.value,
                                                )
                                              }
                                              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white min-h-[100px] resize-y"
                                              placeholder={t(
                                                "tedPlan.edit.content",
                                              )}
                                            />

                                            <div className="flex items-center gap-2">
                                              <button
                                                onClick={handleSaveStep}
                                                disabled={
                                                  updatePlanMutation.isPending
                                                }
                                                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg text-sm font-medium transition-colors"
                                              >
                                                <Save className="w-3 h-3" />
                                                {updatePlanMutation.isPending
                                                  ? t("common.saving")
                                                  : t("common.save")}
                                              </button>
                                              <button
                                                onClick={handleCancelEdit}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors"
                                              >
                                                <X className="w-3 h-3" />
                                                {t("common.cancel")}
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          // Display mode
                                          <>
                                            <div className="flex items-center justify-between">
                                              <h4 className="font-medium text-gray-900 dark:text-white">
                                                {step.yontem_teknik}
                                              </h4>
                                              <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                                                  <Clock className="w-3 h-3" />
                                                  <span>
                                                    {step.sure_dakika}{" "}
                                                    {t("common.time.minutes")}
                                                  </span>
                                                </div>
                                                <button
                                                  onClick={() =>
                                                    handleEditStep(step)
                                                  }
                                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                                                >
                                                  <Edit3 className="w-3 h-3" />
                                                  {t("common.edit")}
                                                </button>
                                              </div>
                                            </div>

                                            <div className="prose prose-sm max-w-none dark:prose-invert">
                                              <CitationParser
                                                content={step.icerik || ""}
                                                additionalMaterials={
                                                  parsedMaterials
                                                }
                                                onCitationClick={
                                                  handleCitationClick
                                                }
                                              />
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Materials Summary */}
        {parsedMaterials.length > 0 && (
          <div className="mt-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("tedPlan.additionalMaterials")}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownloadLessonPlan}
                  className={`flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                    isLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={isLoading}
                >
                  <Package className="w-4 h-4" />
                  {t("tedPlan.download.lessonPlan")}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {parsedMaterials.map((material) => (
                <div
                  key={material.ek_id}
                  className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex-shrink-0 mt-1">
                    {material.toolType === "LESSON_HOOK" ? (
                      <FileText className="w-5 h-5 text-blue-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full dark:bg-blue-900/30 dark:text-blue-300">
                        EK-{material.ek_id}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full dark:bg-gray-600 dark:text-gray-300">
                        {material.toolType === "LESSON_HOOK"
                          ? t("tedPlan.materialType.lessonHook")
                          : t("tedPlan.materialType.misconception")}
                      </span>
                    </div>
                    {material.icerik_json.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {material.icerik_json.description}
                        </Markdown>
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => handleMaterialClick(material)}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                      >
                        <Users className="w-3 h-3" />
                        {t("common.view")}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadMaterial(material);
                        }}
                        className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        {t("common.download")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Citation Modal */}
      <CitationModal
        isOpen={!!selectedCitation}
        onClose={() => setSelectedCitation(null)}
        material={selectedCitation?.material || null}
        description={selectedCitation?.description || ""}
      />
    </div>
  );
};

export default TedPlanDetails;
